import { AsyncPipe, NgClass } from '@angular/common';
import {
  type AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  type OnDestroy,
  computed,
  effect,
  inject,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivationEnd,
  ActivationStart,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { AiAssistantV2Store } from '@bizzdesign/ai-assistant/data-access';
import { AiAssistantContainerV2Component } from '@bizzdesign/ai-assistant/feature';
import {
  BeaconOccluderDirective,
  TrialDevMenuComponent,
  TrialExpiredDialogComponent,
  TrialPopoverStore,
} from '@bizzdesign/feature-trial-experience';
import { BizzdesignSdk } from '@bizzdesign/bizzdesign-sdk';
import { ContentDetailsGlobalStore } from '@bizzdesign/data-access-content-details';
import { WorkspacesService } from '@bizzdesign/data-access-workspaces';
import { FeatureFlagsStore } from '@bizzdesign/shared/feature-flags';
import { type AppVersion, type ToolbarButton } from '@bizzdesign/shared/models';
import {
  AccountToolbarService,
  AppVersionService,
  LayoutContextService,
} from '@bizzdesign/shared/services';
import {
  CreateOnboardingWorkspaceService,
  OnboardingFirstLoginService,
} from '@bizzdesign/workspaces/feature-onboarding';
import {
  DrawerDirective,
  DrawerGlobalStore,
  ToastComponent,
} from '@bizzdesign/ui-components';
import { LoaderComponent } from '@bizzdesign/ui-loader';
import { MessageService } from 'primeng/api';
import {
  type Observable,
  ReplaySubject,
  combineLatest,
  debounce,
  filter,
  from,
  map,
  shareReplay,
  startWith,
  takeUntil,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';
import { slideAnimation } from '../../animations/drawer-slide.animation';
import {
  type LayoutConfig,
  defaultLayoutConfig,
  isLayoutConfig,
} from '../../utils/layout-config.utils';
import { DrawerComponent } from '../drawer/drawer.component';
import { FooterComponent } from '../footer/footer.component';
import { HeaderComponent } from '../header/header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';

@Component({
  selector: 'bd-layout',
  imports: [
    AsyncPipe,
    NgClass,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    RouterModule,
    ToastComponent,
    LoaderComponent,
    DrawerDirective,
    DrawerComponent,
    AiAssistantContainerV2Component,
    TrialDevMenuComponent,
    TrialExpiredDialogComponent,
    BeaconOccluderDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  animations: [slideAnimation()],
})
export class LayoutComponent implements AfterViewInit, OnDestroy {
  readonly #router = inject(Router);
  readonly #sdk = inject(BizzdesignSdk);
  readonly #messageService = inject(MessageService);
  readonly #workspacesService = inject(WorkspacesService);
  readonly drawerStore = inject(DrawerGlobalStore);
  readonly aiAssistantV2Store = inject(AiAssistantV2Store);
  readonly trialPopoverStore = inject(TrialPopoverStore);
  readonly featureFlags = inject(FeatureFlagsStore);
  private readonly accountToolbarService = inject(AccountToolbarService);
  private readonly layoutContextService = inject(LayoutContextService);
  private readonly appVersionService = inject(AppVersionService);
  readonly #onboardingFirstLoginService = inject(OnboardingFirstLoginService);
  readonly #createOnboardingWorkspaceService = inject(
    CreateOnboardingWorkspaceService,
  );
  #onboardingWorkspaceCheckStarted = false;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  layoutConfig$: Observable<LayoutConfig> = this.setupLayoutConfig$();
  layoutContext$ = this.layoutContextService.context$;
  appVersion$: Observable<AppVersion> = from(
    this.appVersionService.getAppVersion(),
  );

  readonly contentDetailsGlobalStore = inject(ContentDetailsGlobalStore);

  readonly aiButtonV2 = computed((): ToolbarButton | undefined => {
    const buttonState = this.aiAssistantV2Store.aiButtonState();

    return {
      tScope: 'featureAiAssistant',
      group: 'ai',
      type: 'tertiary',
      icon: buttonState.icon,
      iconPos: 'left',
      onClick: () => this.aiAssistantV2Store.toggleAssistant(),
      styleClass: '!rounded-full bd-no-capitalize',
      disabled: !this.aiAssistantV2Store.aiAssistantButtonEnabled(),
    };
  });

  private setupLayoutConfig$(): Observable<LayoutConfig> {
    const startConfig$ = this.#router.events.pipe(
      filter((event) => event instanceof ActivationStart),
      tap((event) => {
        this.layoutContextService.context$.next(
          event.snapshot.outlet === 'overlay' ? 'overlay' : 'primary',
        );
      }),
      map((event) => event.snapshot.data),
      map((data) => data?.['layoutConfig']),
      filter((config) => !!config),
      filter(isLayoutConfig),
      startWith(defaultLayoutConfig),
    );
    const endConfig$ = this.#router.events.pipe(
      filter(
        (event) =>
          event instanceof NavigationEnd || event instanceof ActivationEnd,
      ),
      tap((event) => {
        if (event instanceof NavigationEnd) {
          this.layoutContextService.context$.next(
            event.url.includes('(overlay:') ? 'overlay' : 'primary',
          );
        }
      }),
      filter((event) => event instanceof ActivationEnd),
      map((event) => event.snapshot.data),
      map((data) => data?.['layoutConfig']),
      filter((config) => !!config),
    );
    return endConfig$.pipe(
      withLatestFrom(startConfig$),
      map(([endConfig, startConfig]) =>
        // if the end config is 'latestConfig', we take the latest start config,
        // this is useful for error page routes like access-denied which replace
        // the url with the original url and want to keep the same layout config
        endConfig === 'latestConfig'
          ? {
              ...startConfig,
              sidebar: startConfig.sidebar
                ? { ...startConfig.sidebar, selectedItem: '' }
                : undefined,
            }
          : endConfig,
      ),
      filter(isLayoutConfig),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  leftDrawer = combineLatest([
    toObservable(this.drawerStore.leftDrawer),
    this.layoutConfig$,
  ]).pipe(
    debounce(([drawer, config]) =>
      timer(!drawer && config.drawer.animation ? 1000 : 0),
    ),
  );

  rightDrawer = combineLatest([
    toObservable(this.drawerStore.rightDrawer),
    this.layoutConfig$,
  ]).pipe(
    debounce(([drawer, config]) =>
      timer(!drawer && config.drawer.animation ? 1000 : 0),
    ),
  );

  ngAfterViewInit(): void {
    this.#sdk.errorClient.errors$
      .pipe(
        filter(
          (err) =>
            // TODO remove after API allows filtering views on name NGH-3513
            !err?.message?.match(
              /The view with the name '.*' does not exist./g,
            ),
        ),
        tap(() => {
          this.#messageService.add({
            severity: 'error',
            summary: 'toast.errors.genericTitle',
            detail: 'toast.errors.genericDetails',
            data: { tScope: 'uiComponents' },
          });
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    // Not in the frame container: the route is let go at hand-off, so the
    // container is usually gone by the time a save fails.
    this.#workspacesService.frameSaveFailed$
      .pipe(
        tap(() => {
          this.#messageService.add({
            severity: 'error',
            summary: 'saveFrameError.title',
            detail: 'saveFrameError.details',
            data: { tScope: 'featureWorkspaces' },
          });
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  constructor() {
    effect(() => {
      this.accountToolbarService.toolbarPrimaryButton$.next(this.aiButtonV2());
    });

    // TODO: NGH-8776 delete this flow and its service once backend onboarding
    // provisioning is live on every tenant, not just trial ones.
    effect(() => {
      if (this.#onboardingWorkspaceCheckStarted) {
        return;
      }
      if (!this.featureFlags.flagsLoaded()) {
        return;
      }
      // Decide once when flags resolve, for both branches: a later poll that
      // flips the trial flag off must not re-enter the create path.
      this.#onboardingWorkspaceCheckStarted = true;
      if (this.featureFlags.trialExperienceEnabled()) {
        return;
      }
      this.createOnboardingWorkspaceIfFirstLogin();
    });
  }

  private createOnboardingWorkspaceIfFirstLogin(): void {
    this.#onboardingFirstLoginService
      .isRecentFirstLoginWithElevatedRole()
      .then((isRecentFirstLogin) => {
        if (!isRecentFirstLogin) {
          return;
        }

        this.#onboardingFirstLoginService.markOnboardingWorkspaceCreated();
        return this.#createOnboardingWorkspaceService.create();
      })
      .catch(() => {
        // Non-critical - silently fail to avoid blocking the app
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
